import { AlgoliaTemplate } from '@algolia/templates';
import { Component } from '@theme/component';

const DEFAULT_PRODUCT_CARD_SECTION_ID = 'upsell_product_card';
/** Mirrors the `paginate … by 24` cap in `upsell-product-card.liquid`. */
const PRODUCT_CARD_BATCH_LIMIT = 24;
const PRODUCT_CARD_FETCH_TIMEOUT = 10000;

/**
 * Rendered theme product cards keyed by `sectionId:handle`, so a popup that is
 * opened repeatedly only pays for the section render once per handle.
 * @type {Map<string, string>}
 */
const productCardHtmlCache = new Map();

/**
 * Cache keys the section answered for but could not render — a product that is
 * not in the search index, for example. Kept apart from failed requests, which
 * stay absent from both collections so the next open retries them.
 * @type {Set<string>}
 */
const productCardMisses = new Set();

/**
 * Resolves the section that renders the theme product card. Mirrors the lookup
 * in `atc-upsell-popup.js` so both popups share one configurable card.
 * @returns {string}
 */
const getProductCardSectionId = () => {
  const fromSlide = document
    .querySelector('[data-upsell-product-card-section-id]')
    ?.getAttribute('data-upsell-product-card-section-id');
  if (fromSlide) return fromSlide;

  const sectionElement = document.querySelector(
    '.upsell-product-card-section[id^="shopify-section-"]'
  );
  const sectionId = sectionElement?.id?.replace('shopify-section-', '');
  if (sectionId) return sectionId;

  return DEFAULT_PRODUCT_CARD_SECTION_ID;
};

/**
 * Builds a field-scoped storefront search query for a set of hits. Product ids
 * keep the URL short (`id:` ≈ 16 chars vs. handles well over 100), handles are
 * the fallback for records that ship without an id.
 * @param {any[]} hits
 * @returns {string}
 */
const buildProductCardQuery = (hits) =>
  hits.map((hit) => (hit.id ? `id:${hit.id}` : `handle:${hit.handle}`)).join(' OR ');

/**
 * Fetches the theme product card for every hit in a single request.
 *
 * The search route is used because it is the only storefront route that can put
 * an arbitrary product list into Liquid scope — `upsell-product-card.liquid`
 * detects the `id:` query and renders one `.js-product-slide` per result.
 *
 * @param {any[]} hits
 * @param {string} sectionId
 * @param {AbortSignal} [signal]
 * @returns {Promise<Map<string, string>>} slide markup keyed by product handle
 */
const fetchProductCardSlides = async (hits, sectionId, signal) => {
  /** @type {Map<string, string>} */
  const slidesByHandle = new Map();
  if (!hits.length) return slidesByHandle;

  const base = window.Shopify?.routes?.root || '/';
  const params = new URLSearchParams({
    type: 'product',
    q: buildProductCardQuery(hits),
    sections: sectionId,
  });

  const response = await fetch(`${base}search?${params}`, { signal });
  if (!response.ok) {
    throw new Error(`Product card section responded with ${response.status}`);
  }

  const data = await response.json();
  const html = data?.[sectionId] ?? Object.values(data ?? {})[0];
  if (typeof html !== 'string' || !html) return slidesByHandle;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const slide of doc.querySelectorAll('.js-product-slide')) {
    const handle = slide.getAttribute('data-product-handle');
    const content = slide.innerHTML.trim();
    if (handle && content) slidesByHandle.set(handle, content);
  }

  return slidesByHandle;
};

class AlgoliaProductRecommendations extends Component {
  requiredRefs = ['slideshow-template', 'slideshow-wrapper'];
  connectedCallback() {
    super.connectedCallback();
    this.#initVariables();
    this.init();
  }

  #initVariables() {
    this.productID = this.dataset.productId;
    this.sectionID = this.dataset.sectionId;
    this.productCount = parseInt(this.dataset.productCount || '10', 10);
    this.algoliaAppId = this.dataset.appId;
    this.algoliaSearchApiKey = this.dataset.apiKey;
    this.algoliaIndex = this.dataset.indexName;
    this.algoliaModel = this.dataset.model;
    // Gated by settings.enable_upsell_alternative_product_card, so this popup and
    // the ATC upsell popup render the same theme product card.
    this.useThemeProductCard = this.dataset.useThemeProductCard === 'true';
    // Get parent product_type from data attribute (should be a string)
    const parentProductType = this.dataset.productType || '';
    this.typeFilter = parentProductType
      ? `("product_type":"${parentProductType}") AND ("inventory_available":"true")`
      : '("inventory_available":"true")';
    const parentVendor = this.dataset.productVendor || '';
    const escapedVendor = parentVendor.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    const vendorFilter = parentVendor
      ? `("vendor":"GastroHero" OR "vendor":"${escapedVendor}")`
      : '("vendor":"GastroHero")';
    this.typeFilter += ` AND ${vendorFilter}`;
  }
  async init() {
    // UZ-8543: outside collection/search pages the libraries are loaded on demand
    await window.loadAlgoliaLibs?.().catch(() => {});
    if (!window.algoliasearch || (!this.useThemeProductCard && !AlgoliaTemplate.hitTemplate)) {
      console.error('Algolia libraries not found');
      return;
    }
    if (
      !this.algoliaAppId ||
      !this.algoliaSearchApiKey ||
      !this.algoliaIndex ||
      !this.algoliaModel
    ) {
      console.error(
        'Algolia configuration not found',
        this.algoliaAppId,
        this.algoliaSearchApiKey,
        this.algoliaIndex,
        this.algoliaModel
      );
      return;
    }
    try {
      const client = window.algoliasearch
        .algoliasearch(this.algoliaAppId, this.algoliaSearchApiKey)
        .initRecommend();
      const algoliaResponse = await client.getRecommendations({
        requests: [
          {
            indexName: this.algoliaIndex,
            objectID: this.productID,
            model: this.algoliaModel,
            threshold: 1,
            maxRecommendations: this.productCount,
            queryParameters: {
              filters: this.typeFilter,
            },
            fallbackParameters: {
              filters: this.typeFilter,
            },
          },
        ],
      });
      const { hits } = algoliaResponse.results?.[0] || {};
      const availableHits = hits
        .filter((hit) => {
          // Product-level availability
          if (hit.available === true) return true;

          // Numeric stock flags
          if (hit.inStock === 1) return true;
          if (typeof hit.inventory_quantity === 'number' && hit.inventory_quantity > 0) return true;
          if (typeof hit.variants_inventory_count === 'number' && hit.variants_inventory_count > 0)
            return true;

          // Variant-level availability (very common)
          if (Array.isArray(hit.variants)) {
            return hit.variants.some(
              (v) =>
                v.available === true ||
                (typeof v.inventory_quantity === 'number' && v.inventory_quantity > 0)
            );
          }

          return false;
        })
        .slice(0, this.productCount);

      const algoliaHits = availableHits;
      if (algoliaHits.length === 0) {
        this.style.display = 'none';
      } else {
        await this.loadRecommendationSlider(algoliaHits);
      }
    } catch (e) {
      this.style.display = 'none';
      console.error('Error initializing Algolia Product Recommendations:', e);
    }
  }
  /**
   * Renders the theme product card section for every hit in one request. Returns
   * an empty array when the section is unavailable so the caller can fall back
   * to the Algolia card.
   * @param {any[]} hits
   * @returns {Promise<string[]>}
   */
  async buildThemeCardSlides(hits) {
    const candidates = hits.filter((hit) => hit?.handle).slice(0, PRODUCT_CARD_BATCH_LIMIT);
    if (candidates.length === 0) return [];

    const sectionId = getProductCardSectionId();
    const cacheKeyOf = (/** @type {any} */ hit) => `${sectionId}:${hit.handle}`;
    const missing = candidates.filter((hit) => {
      const cacheKey = cacheKeyOf(hit);
      return !productCardHtmlCache.has(cacheKey) && !productCardMisses.has(cacheKey);
    });

    if (missing.length > 0) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), PRODUCT_CARD_FETCH_TIMEOUT);

      try {
        const slidesByHandle = await fetchProductCardSlides(missing, sectionId, controller.signal);

        for (const [handle, content] of slidesByHandle) {
          productCardHtmlCache.set(`${sectionId}:${handle}`, content);
        }

        // The request succeeded, so anything still missing has no card to render.
        for (const hit of missing) {
          if (!slidesByHandle.has(hit.handle)) productCardMisses.add(cacheKeyOf(hit));
        }
      } catch (e) {
        // Left out of both caches on purpose — the next open retries.
        console.error('Error loading theme product cards:', e);
      } finally {
        clearTimeout(timeout);
      }
    }

    // Search results come back by relevance, so re-order by the Algolia ranking.
    /** @type {string[]} */
    const slides = [];
    for (const hit of candidates) {
      const content = productCardHtmlCache.get(cacheKeyOf(hit));
      if (content) slides.push(content);
    }

    return slides;
  }

  /** @param {any[]} hits @returns {Promise<string[]>} */
  async buildSlides(hits) {
    if (this.useThemeProductCard) {
      const slides = await this.buildThemeCardSlides(hits);
      if (slides.length) return slides;
    }

    if (!AlgoliaTemplate.hitTemplate) {
      console.error('Algolia hit template not found');
      return [];
    }

    return hits.map((hit) => AlgoliaTemplate.hitTemplate(hit)).filter(Boolean);
  }

  async loadRecommendationSlider(hits) {
    let slideshowWrapper = this.refs['slideshow-wrapper'];
    if (Array.isArray(slideshowWrapper)) {
      slideshowWrapper = slideshowWrapper[0];
    }
    let slideshowTemplate = this.refs['slideshow-template'];
    if (Array.isArray(slideshowTemplate)) {
      slideshowTemplate = slideshowTemplate[0];
    }

    if (
      !slideshowTemplate ||
      !slideshowWrapper ||
      !(slideshowTemplate instanceof HTMLTemplateElement)
    ) {
      return;
    }
    const fragment = slideshowTemplate.content.cloneNode(true);
    if (!fragment) {
      console.error('Failed to clone Algolia template');
      return;
    }
    const slideshow = fragment.querySelector('slideshow-component');
    const scroller = fragment.querySelector('slideshow-slides');
    if (!slideshow || !scroller) return;
    const slides = await this.buildSlides(hits);
    if (slides.length === 0) {
      this.style.display = 'none';
      return;
    }
    const slidesHTML = slides.map((content, index) => this.buildSlide(content, index)).join('');
    scroller.innerHTML = slidesHTML;
    const slideCount = scroller.querySelectorAll('slideshow-slide').length;
    slideshow.style.setProperty('--slideshow-timeline', this.buildTimelineScope(slideCount));
    slideshow.setAttribute('initial-slide', '0');

    slideshowWrapper.replaceChildren(fragment);
  }
  buildSlide(content, index) {
    return `<slideshow-slide
    ref="slides[]"
    class="resource-list__slide "
    aria-hidden="${index === 0 ? 'false' : 'true'}"
    style="--slideshow-timeline: --slide-${index};"
  >
    ${this.wrapSlideContent(content)}
  </slideshow-slide>`;
  }
  wrapSlideContent(content) {
    return content.includes('resource-list__item')
      ? content
      : `<div class="resource-list__item">${content}</div>`;
  }
  buildTimelineScope(slideCount) {
    return Array.from({ length: slideCount }, (_, index) => `--slide-${index}`).join(', ');
  }
}

if (!customElements.get('algolia-product-recommendations')) {
  customElements.define('algolia-product-recommendations', AlgoliaProductRecommendations);
}
